import { useState, useMemo, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { createRegion } from '@/services/regions'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { RegionType } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function RegionCreate() {
  const { nationId } = useParams<{ nationId: string }>()
  const navigate = useNavigate()
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()
  const { nation } = useNation(nationId)
  const { world } = useWorld(nation?.world_id)

  // Form fields
  const [name, setName] = useState('')
  const [regionType, setRegionType] = useState<RegionType>('plains')
  const [seatCount, setSeatCount] = useState('0')
  const [population, setPopulation] = useState('0')

  // Form state
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
    }
    items.push({ label: 'Create Region' })
    return items
  }, [world, nation])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!name.trim()) {
      setError('Name is required')
      return
    }

    const seats = Number(seatCount)
    if (isNaN(seats) || seats < 0) {
      setError('Seat count must be a non-negative number')
      return
    }

    const pop = Number(population)
    if (isNaN(pop) || pop < 0) {
      setError('Population must be a non-negative number')
      return
    }

    if (!nationId) {
      setError('Nation ID is missing')
      return
    }

    setIsSubmitting(true)

    try {
      const region = await createRegion({
        nation_id: nationId,
        name: name.trim(),
        region_type: regionType,
        seat_count: seats,
        population: pop,
        economic_profile: {},
      })
      navigate(`/regions/${region.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create region')
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(`/nations/${nationId}`)
  }

  // Show loading while checking admin status
  if (isAdminLoading) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </AppLayout>
    )
  }

  // Redirect non-admins
  if (!isAdmin) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Only administrators can create new regions.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate(`/nations/${nationId}`)}>
              Back to Nation
            </Button>
          </CardFooter>
        </Card>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Card className="mx-auto max-w-2xl">
        <CardHeader>
          <CardTitle>Create New Region</CardTitle>
          <CardDescription>Add a new administrative region to this nation.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            {error && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter region name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="regionType">Region Type</Label>
              <Select
                value={regionType}
                onValueChange={(value: string) => setRegionType(value as RegionType)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="regionType">
                  <SelectValue placeholder="Select region type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coastal">Coastal</SelectItem>
                  <SelectItem value="mountain">Mountain</SelectItem>
                  <SelectItem value="plains">Plains</SelectItem>
                  <SelectItem value="forest">Forest</SelectItem>
                  <SelectItem value="urban_core">Urban Core</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">The geographic type of this region.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seatCount">Seat Count</Label>
              <Input
                id="seatCount"
                type="number"
                min="0"
                placeholder="0"
                value={seatCount}
                onChange={(e) => setSeatCount(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-muted-foreground text-xs">
                Number of legislative seats allocated to this region.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="population">Population</Label>
              <Input
                id="population"
                type="number"
                min="0"
                placeholder="0"
                value={population}
                onChange={(e) => setPopulation(e.target.value)}
                disabled={isSubmitting}
              />
              <p className="text-muted-foreground text-xs">Total population of this region.</p>
            </div>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Region'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </AppLayout>
  )
}
